'use client'

import { Alert, Button, Group, Modal, Stack, Text } from '@mantine/core'
import { useEffect, useState } from 'react'
import { Icon } from '@/components/icon'
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons'

// The loud half of DEV-1396: an export that downloaded with blank cells must
// interrupt the user — a toast is dismissable by inattention. showExportWarnings
// is a plain function so the shared export mutation (not a component) can raise
// the modal; the single host below is mounted once in app providers.

let listeners: ((warnings: string[]) => void)[] = []

export const showExportWarnings = (warnings: string[]) => {
  listeners.forEach((listener) => listener(warnings))
}

export const ExportWarningsModalHost = () => {
  const [warnings, setWarnings] = useState<string[] | null>(null)

  useEffect(() => {
    const listener = (next: string[]) => setWarnings(next)
    listeners.push(listener)
    return () => {
      listeners = listeners.filter((l) => l !== listener)
    }
  }, [])

  const close = () => setWarnings(null)

  return (
    <Modal
      opened={!!warnings?.length}
      onClose={close}
      title={
        <Group gap="xs">
          <Icon
            icon={faTriangleExclamation}
            size={22}
            color="var(--mantine-color-yellow-7)"
          />
          <Text fw={600}>Export downloaded — some values are blank</Text>
        </Group>
      }
      centered
      size="70%"
      // Deliberate acknowledgement required: clicking away must not dismiss it.
      closeOnClickOutside={false}
    >
      <Stack gap="md">
        <Text size="sm">
          The file was downloaded, but these cross-references are missing, so
          the matching columns were left blank:
        </Text>
        <Alert color="yellow" p="md">
          <Stack gap={6}>
            {(warnings ?? []).map((warning) => (
              <Text size="sm" key={warning}>
                {warning}
              </Text>
            ))}
          </Stack>
        </Alert>
        <Text size="sm" c="dimmed">
          You can use the file as is, or add the missing cross-references on the
          Price Sync page and export again.
        </Text>
        <Group justify="flex-end">
          <Button onClick={close}>OK</Button>
        </Group>
      </Stack>
    </Modal>
  )
}
